import React, { useMemo, useState, useEffect } from 'react';

import BasicCRUD from './../core/basic-crud';
import FadeAlert from "../core/fade-alert";
import Dialog from "../core/dialogs";
import PlanCard from "../core/plan-card";
import InvoicePreviewDialog from "../core/invoice-preview";
import { PRODUCT_ID, AUTH_URL } from "../../const";
import { useAuth } from "../../AuthContext";
import { socket, requestData } from "../../socket";

const PLAN_STYLE_PRESETS = [
    {
        presetId: "starter",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 60%, #eef2ff 100%)",
        isDark: false,
        badgeKey: null,
    },
    {
        presetId: "growth",
        background: "linear-gradient(135deg, #5b8def 0%, #7f6bff 55%, #a855f7 100%)",
        isDark: true,
        badgeKey: "popular",
    },
    {
        presetId: "enterprise",
        background: "linear-gradient(135deg, #0b1023 0%, #1b1f3b 50%, #2d1b69 100%)",
        isDark: true,
        badgeKey: "premium",
    },
];

// -------- i18n --------
const dict = {
    en: {
        // page
        title: "Invoices",
        subTitle: "add / edit invoices",
        homeURL: "/dashboard",
        addTitle: "Create Invoice",
        editTitle: "Edit Invoice",

        // columns
        colPlan: "Pricing plan / period",
        colCost: "Amount",
        colStatus: "Status",

        // cells
        noPlan: "No Plan",
        na: "N/A",
        dateRange: (from, to) => `${from} → ${to}`,

        // statuses
        statusMap: {
            pending: "Pending",
            paid: "Paid",
            canceled: "Canceled",
            unknown: "Unknown",
        },

        // form tab + fields
        tabMain: "Main",
        fID: "ID",
        fWorkspaceID: "Workspace ID",
        fProductID: "Product ID",
        fPlanID: "Pricing Plan ID",
        fPlanName: "Pricing Plan Name",
        fAmount: "Amount",
        fCurrency: "Currency",
        fPeriod: "Period",
        fDescription: "Description",
        fDueDate: "Due Date",
        fMeta: "Meta (JSON)",

        // toasts
        toastEntity: "Invoice",
        createdOk: "created successfully",
        deletedOk: "canceled successfully",
        cancelAction: "Cancel invoice",
        viewAction: "View invoice",
        previewTitle: "Invoice details",
        previewIssued: "Issued",
        previewDue: "Due date",
        previewStatus: "Status",
        previewAmount: "Amount",
        previewPlan: "Plan",
        previewWorkspace: "Workspace",
        previewMeta: "Metadata",
        previewDescription: "Description",
        close: "Close",
        previewFeatures: "Plan options",
        choosePlanTitle: "Choose a plan",
        selectPlanPlaceholder: "Select a plan to continue",
        loadingPlans: "Loading available plans…",
        failedLoadPlans: "Unable to load plans now.",
        invoiceCreated: "Invoice request sent!",
        invoiceFailed: "Unable to create invoice. Try again.",
        creatingInvoice: "Creating…",
        maybeLater: "Cancel",
        btnSelectPlan: "Select plan",
        btnSelected: "Selected",
        selectedPlanLabel: "Selected plan",
        choosePlanSubtitle: "Pick the plan you'd like to invoice for.",
        continueWith: (plan) => `Continue with ${plan}`,
        upgradePlaceholder: "We’ll finalize the upgrade with you soon — API integration is on the way.",
        badgePopular: "Popular",
        badgePremium: "Premium",
        payInvoice: "Pay invoice",
        payInvoiceSoon: "Payments will be available soon.",
    },

    ru: {
        title: "Счета",
        subTitle: "добавление / редактирование счетов",
        homeURL: "/dashboard",
        addTitle: "Добавить счёт",
        editTitle: "Редактировать счёт",

        colPlan: "Тариф / период",
        colCost: "Сумма",
        colStatus: "Статус",

        noPlan: "Нет плана",
        na: "Н/Д",
        dateRange: (from, to) => `${from} → ${to}`,

        statusMap: {
            pending: "В ожидании",
            paid: "Оплачено",
            canceled: "Отменён",
            unknown: "Неизвестно",
        },

        tabMain: "Основное",
        fWorkspaceID: "ID воркспейса",
        fProductID: "ID продукта",
        fPlanID: "ID тарифного плана",
        fPlanName: "Название тарифного плана",
        fAmount: "Сумма",
        fCurrency: "Валюта",
        fPeriod: "Период",
        fDescription: "Описание",
        fDueDate: "Дата оплаты",
        fMeta: "Мета (JSON)",

        toastEntity: "Счёт",
        createdOk: "успешно создан",
        deletedOk: "успешно отменён",
        cancelAction: "Отменить счёт",
        viewAction: "Просмотреть счёт",
        previewTitle: "Детали счёта",
        previewIssued: "Выставлен",
        previewDue: "Дата оплаты",
        previewStatus: "Статус",
        previewAmount: "Сумма",
        previewPlan: "Тариф",
        previewWorkspace: "Воркспейс",
        previewMeta: "Метаданные",
        previewDescription: "Описание",
        close: "Закрыть",
        previewFeatures: "Опции тарифа",
        choosePlanTitle: "Выберите тариф",
        selectPlanPlaceholder: "Выберите тариф, чтобы продолжить",
        loadingPlans: "Загружаем доступные тарифы…",
        failedLoadPlans: "Не удалось загрузить тарифы.",
        invoiceCreated: "Запрос на счёт отправлен!",
        invoiceFailed: "Не удалось создать счёт.",
        creatingInvoice: "Создаём…",
        maybeLater: "Отмена",
        btnSelectPlan: "Выбрать тариф",
        btnSelected: "Выбран",
        selectedPlanLabel: "Выбранный тариф",
        choosePlanSubtitle: "Выберите тариф, по которому нужно выставить счёт.",
        continueWith: (plan) => `Продолжить с «${plan}»`,
        upgradePlaceholder: "Скоро подключим API и завершим оформление вместе с вами.",
        badgePopular: "Популярно",
        badgePremium: "Премиум",
        payInvoice: "Оплатить счёт",
        payInvoiceSoon: "Скоро появится возможность оплатить счёт.",
    },

    ua: {
        title: "Рахунки",
        subTitle: "додавання / редагування рахунків",
        homeURL: "/dashboard",
        addTitle: "Додати рахунок",
        editTitle: "Редагувати рахунок",

        colPlan: "Тариф / період",
        colCost: "Сума",
        colStatus: "Статус",

        noPlan: "Немає плану",
        na: "Н/Д",
        dateRange: (from, to) => `${from} → ${to}`,

        statusMap: {
            pending: "Очікує оплати",
            paid: "Сплачено",
            canceled: "Скасовано",
            unknown: "Невідомо",
        },

        tabMain: "Основне",
        fWorkspaceID: "ID воркспейсу",
        fProductID: "ID продукту",
        fPlanID: "ID тарифного плану",
        fPlanName: "Назва тарифного плану",
        fAmount: "Сума",
        fCurrency: "Валюта",
        fPeriod: "Період",
        fDescription: "Опис",
        fDueDate: "Дата оплати",
        fMeta: "Мета (JSON)",

        toastEntity: "Рахунок",
        createdOk: "успішно створено",
        deletedOk: "успішно скасовано",
        cancelAction: "Скасувати рахунок",
        viewAction: "Переглянути рахунок",
        previewTitle: "Деталі рахунку",
        previewIssued: "Створено",
        previewDue: "Дата оплати",
        previewStatus: "Статус",
        previewAmount: "Сума",
        previewPlan: "Тариф",
        previewWorkspace: "Робочий простір",
        previewMeta: "Метадані",
        previewDescription: "Опис",
        close: "Закрити",
        previewFeatures: "Опції тарифу",
        choosePlanTitle: "Оберіть тариф",
        selectPlanPlaceholder: "Оберіть тариф, щоб продовжити",
        loadingPlans: "Завантажуємо тарифи…",
        failedLoadPlans: "Не вдалося завантажити тарифи.",
        invoiceCreated: "Запит на рахунок відправлено!",
        invoiceFailed: "Не вдалося створити рахунок.",
        creatingInvoice: "Створюємо…",
        maybeLater: "Скасувати",
        btnSelectPlan: "Обрати тариф",
        btnSelected: "Обрано",
        selectedPlanLabel: "Обраний тариф",
        choosePlanSubtitle: "Оберіть тариф, на який потрібно виставити рахунок.",
        continueWith: (plan) => `Продовжити з «${plan}»`,
        upgradePlaceholder: "Невдовзі додамо API і завершимо оформлення разом із вами.",
        badgePopular: "Популярно",
        badgePremium: "Преміум",
        payInvoice: "Оплатити рахунок",
        payInvoiceSoon: "Незабаром буде доступна оплата рахунку.",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- helpers ----------
const usDate = (s, fallback = "N/A") => {
    if (!s) return fallback;
    const d = new Date(s);
    if (isNaN(d)) return fallback;
    return d.toLocaleDateString('en-US');
};

// ---------- component ----------
const Invoices = ({ lang: langProp, ...props }) => {
    const { user, refreshUser } = useAuth();
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "invoices";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [planModalOpen, setPlanModalOpen] = useState(false);
    const [plansLoading, setPlansLoading] = useState(false);
    const [plansError, setPlansError] = useState(null);
    const [plansRequested, setPlansRequested] = useState(false);
    const [pricingPlans, setPricingPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
    const [invoiceError, setInvoiceError] = useState(null);
    const [payingInvoice, setPayingInvoice] = useState(false);
    const [payError, setPayError] = useState(null);

    const formatPlanRange = (data) => {
        const planName = data['pricing_plan_name'] || t.noPlan;
        const period = data.period ? ` · ${data.period}` : "";
        const issuedAt = usDate(data['issued_at'], t.na);
        const dueDate = usDate(data['due_date'], t.na);
        const rawDescription = (data.description || "").trim();
        const normalizedPlan = (planName || "").trim().toLowerCase();
        const showDescription =
            rawDescription &&
            rawDescription.toLowerCase() !== normalizedPlan;
        const handleOpenPreview = () => openPreview(data);
        const handleKeyDown = (evt) => {
            if (evt.key === "Enter" || evt.key === " ") {
                evt.preventDefault();
                openPreview(data);
            }
        };

        const styles = {
            container: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                cursor: 'pointer',
                gap: 2,
            },
            planName: { fontWeight: 'bold', fontSize: 16, marginBottom: 4 },
            description: { fontSize: 13, color: '#495057', marginBottom: 4 },
            date: { fontSize: 12, color: '#6c757d' },
        };

        return (
            <div
                style={styles.container}
                role="button"
                tabIndex={0}
                onClick={handleOpenPreview}
                onKeyDown={handleKeyDown}
                aria-label={t.previewTitle}
            >
                <span style={styles.planName}>{planName}{period}</span>
                {showDescription ? <span style={styles.description}>{rawDescription}</span> : null}
                <span style={styles.date}>{t.dateRange(issuedAt, dueDate)}</span>
            </div>
        );
    };

    const StatusBadge = ({ status }) => {
        const normalized = (status || '').toLowerCase();
        const colors = {
            pending: { background: '#FFC107', color: '#000000' },
            paid: { background: '#4CAF50', color: '#FFFFFF' },
            canceled: { background: '#F44336', color: '#FFFFFF' },
            unknown: { background: '#9E9E9E', color: '#FFFFFF' },
        };
        const label = t.statusMap[normalized] || t.statusMap.unknown;
        const style = {
            display: 'inline-block',
            padding: '5px 10px',
            borderRadius: 20,
            fontWeight: 'bold',
            fontSize: 14,
            textAlign: 'center',
            ...(colors[normalized] || colors.unknown),
        };
        return <span style={style}>{label}</span>;
    };

    const formatStatus = (data) => {
        const status = data.status || 'unknown';
        return <StatusBadge status={status} />;
    };

    const getAmountLabel = (amount, currency) => {
        if (amount === undefined || amount === null || amount === "") return t.na;
        return `${currency || "$"}${parseFloat(amount).toFixed(2)}`;
    };

    const PriceDisplay = ({ amount, currency }) => {
        const formattedAmount = getAmountLabel(amount, currency);
        if (formattedAmount === t.na) {
            return <span>{t.na}</span>;
        }
        const style = {
            display: 'block',
            padding: '5px 10px',
            borderRadius: 8,
            backgroundColor: '#f5f5f5',
            color: '#333',
            fontWeight: 'bold',
            fontSize: 14,
            border: '1px solid #ddd',
            textAlign: 'right',
            minWidth: 90,
        };
        return <span style={style}>{formattedAmount}</span>;
    };

    const formatPrice = (data) => {
        const currency = data.currency || data.currency_symbol || "$";
        return <PriceDisplay amount={data.amount ?? data.pricing_plan_price} currency={currency} />;
    };

    const formatDateTime = (value, fallback = t.na) => {
        if (!value) return fallback;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return fallback;
        return date.toLocaleString();
    };

    const formatMetaValue = (meta) => {
        if (!meta) return t.na;
        if (typeof meta === "object") {
            return <pre className="bg-light rounded-2 p-3 small w-100">{JSON.stringify(meta, null, 2)}</pre>;
        }
        try {
            const parsed = JSON.parse(meta);
            return <pre className="bg-light rounded-2 p-3 small w-100">{JSON.stringify(parsed, null, 2)}</pre>;
        } catch {
            return <pre className="bg-light rounded-2 p-3 small w-100">{meta}</pre>;
        }
    };

    const parseInvoiceMeta = (meta) => {
        if (!meta) return {};
        if (typeof meta === "object") return meta;
        try {
            return JSON.parse(meta);
        } catch {
            return {};
        }
    };

    const extractPlanFeatures = (meta) => {
        const metaObj = parseInvoiceMeta(meta);
        const features = metaObj?.plan_features;
        if (Array.isArray(features)) {
            return features.filter(Boolean);
        }
        const display = metaObj?.plan_options?.display;
        if (Array.isArray(display)) {
            return display
                .map((item) => {
                    if (!item) return null;
                    const label = item.label || "";
                    const value = item.value || "";
                    return `${label}${label && value ? ": " : ""}${value}`.trim();
                })
                .filter(Boolean);
        }
        const program = metaObj?.plan_options?.program;
        if (program && typeof program === "object") {
            return Object.entries(program)
                .map(([key, value]) => `${key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}: ${value}`)
                .filter(Boolean);
        }
        return [];
    };

const getPlanDescription = (invoice) => {
    const metaObj = parseInvoiceMeta(invoice.meta);
    return invoice.description || metaObj?.plan_description || "";
};

const isInvoicePending = (invoice) => ((invoice?.status || "").toLowerCase() === "pending");

    const openPreview = (invoice) => setPreviewInvoice(invoice);
    const closePreview = () => {
        setPreviewInvoice(null);
        setPayError(null);
        setPayingInvoice(false);
    };

    const openPlanModal = () => {
        setPlanModalOpen(true);
        setInvoiceError(null);
    };

    const closePlanModal = () => {
        setPlanModalOpen(false);
        setInvoiceError(null);
    };

    const cancelExistingInvoices = async () => {
        try {
            const response = await fetch(`${AUTH_URL}billing/invoices/cancel-all`, {
                method: "POST",
                headers: {
                    Authorization: user.token,
                    "Content-Type": "application/json",
                    "Accept-Language": lang,
                },
                body: JSON.stringify({
                    product_id: PRODUCT_ID,
                    workspace_id: user?.workspace_id || user?.workspace?.id || undefined,
                }),
            });
            const data = await response.json();
            if (!response.ok || data?.status !== 1) {
                throw new Error(data?.err?.message || data?.message || "Unable to cancel invoices");
            }
        } catch (err) {
            console.warn("Failed to cancel previous invoices:", err?.message || err);
        }
    };

    const handleConfirmPlan = async () => {
        if (!selectedPlan || invoiceSubmitting) return;
        setInvoiceSubmitting(true);
        setInvoiceError(null);

        const rawPlan = selectedPlan.raw || {};
        const dueDate = new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString();
        const parseAmount = () => {
            if (typeof rawPlan.price === "number") return rawPlan.price;
            if (typeof rawPlan.price === "string") {
                const parsed = parseFloat(rawPlan.price);
                if (!Number.isNaN(parsed)) return parsed;
            }
            if (typeof selectedPlan.price === "string") {
                const sanitized = parseFloat(selectedPlan.price.replace(/[^\d.,-]/g, "").replace(",", "."));
                if (!Number.isNaN(sanitized)) return sanitized;
            }
            return 0;
        };

        const payload = {
            product_id: PRODUCT_ID,
            workspace_id: user?.workspace_id || user?.workspace?.id || undefined,
            pricing_plan_id: rawPlan.id,
            amount: parseAmount(),
            currency: rawPlan.currency || "$",
            period: rawPlan.period || selectedPlan.period,
            description: rawPlan.description || selectedPlan.description || selectedPlan.name,
            due_date: dueDate,
            meta: {
                plan_name: selectedPlan.name,
                plan_id: rawPlan.id,
                plan_description: selectedPlan.description || rawPlan.description || "",
                plan_features: Array.isArray(selectedPlan.features) ? selectedPlan.features : [],
                source: "invoices-create-dialog",
            },
        };

        try {
            await cancelExistingInvoices();

            const response = await fetch(`${AUTH_URL}billing/invoices`, {
                method: "POST",
                headers: {
                    Authorization: user.token,
                    "Content-Type": "application/json",
                    "Accept-Language": lang,
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (!response.ok || data?.status !== 1) {
                throw new Error(data?.err?.message || data?.message || t.invoiceFailed);
            }

            const createdInvoice = data?.invoice || data?.data || null;
            if (createdInvoice) {
                setPreviewInvoice(createdInvoice);
                const numericAmount = Number(createdInvoice.amount ?? createdInvoice.pricing_plan_price);
                if (!Number.isNaN(numericAmount) && numericAmount === 0) {
                    handlePayInvoice(createdInvoice);
                }
            }

            setResponse({ status: 1, created: 1 });
            setPlanModalOpen(false);
            setSelectedPlanId(null);
            if (typeof window !== "undefined" && window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent("billing:invoices:refreshCounters", { detail: { reason: "created" } }));
            }
            requestData(user, object, props.args || {}, "list");
        } catch (err) {
            console.error("Invoice creation failed", err);
            setInvoiceError(err?.message || t.invoiceFailed);
        } finally {
            setInvoiceSubmitting(false);
        }
    };

    const handleTableAction = (action, item) => {
        if (action === "view") {
            openPreview(item);
        }
    };

    const normalizeFieldsBeforeSave = (data = {}) => {
        const next = { ...data };
        if (next.amount !== undefined && next.amount !== null && next.amount !== "") {
            const parsedAmount = parseFloat(next.amount);
            if (!Number.isNaN(parsedAmount)) {
                next.amount = parsedAmount;
            }
        }
        if (next.due_date) {
            const parsedDate = new Date(next.due_date);
            if (!Number.isNaN(parsedDate.getTime())) {
                next.due_date = parsedDate.toISOString();
            }
        }
        return next;
    };

    const planOptions = useMemo(() => {
        if (!pricingPlans.length) return [];

        const normalizeFeatures = (plan) => {
            const displayItems = Array.isArray(plan?.options?.display) ? plan.options.display : [];
            if (displayItems.length) {
                return displayItems.map((item) => {
                    const label = item.label || "";
                    const value = item.value || "";
                    return [label, value].filter(Boolean).join(": ").trim();
                }).filter(Boolean);
            }
            const programEntries = Object.entries(plan?.options?.program || {});
            if (programEntries.length) {
                return programEntries
                    .map(([key, value]) =>
                        `${key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}: ${value}`
                    )
                    .filter(Boolean);
            }
            return [];
        };

        const formatPriceLabel = (plan) => {
            if (plan?.price === null || typeof plan?.price === "undefined" || plan?.price === "") return null;
            const amount = Number(plan.price);
            const formatted = Number.isNaN(amount)
                ? plan.price
                : amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
            return `${plan.currency || ""}${formatted}`;
        };

        return pricingPlans.map((plan, index) => {
            const preset = PLAN_STYLE_PRESETS[index % PLAN_STYLE_PRESETS.length] || PLAN_STYLE_PRESETS[0];
            return {
                ...preset,
                id: `${plan.id ?? `plan-${index}`}`,
                name: plan.name || "",
                description: plan.description || "",
                price: formatPriceLabel(plan),
                period: plan.period || "",
                features: normalizeFeatures(plan),
                badge: preset.badgeKey === "popular" ? t.badgePopular : preset.badgeKey === "premium" ? t.badgePremium : null,
                raw: plan,
            };
        });
    }, [pricingPlans, t]);

    const selectedPlan = useMemo(() => {
        if (!selectedPlanId) return null;
        return planOptions.find((plan) => plan.id === selectedPlanId) || null;
    }, [planOptions, selectedPlanId]);

    useEffect(() => {
        if (!planOptions.length) {
            if (selectedPlanId) setSelectedPlanId(null);
            return;
        }
        if (!selectedPlanId) return;
        const exists = planOptions.some((plan) => plan.id === selectedPlanId);
        if (!exists) setSelectedPlanId(null);
    }, [planOptions, selectedPlanId]);

    useEffect(() => {
        if (!planModalOpen) {
            setPlansRequested(false);
        }
    }, [planModalOpen]);

    useEffect(() => {
        if (!previewInvoice) {
            setPayError(null);
            setPayingInvoice(false);
        }
    }, [previewInvoice]);

    useEffect(() => {
        if (!planModalOpen) return undefined;
        const responseEvent = "pricing_plans:list-data";

        const handlePlansData = (payload = {}) => {
            setPlansLoading(false);
            if (payload?.status === 1) {
                const plans =
                    (Array.isArray(payload?.plans) && payload.plans) ||
                    (Array.isArray(payload?.data?.plans) && payload.data.plans) ||
                    (Array.isArray(payload?.data) && payload.data) ||
                    [];
                setPricingPlans(plans);
                setPlansError(null);
            } else if (payload) {
                setPlansError(payload?.err?.message || t.failedLoadPlans);
            }
        };

        socket.on(responseEvent, handlePlansData);

        return () => {
            socket.off(responseEvent, handlePlansData);
        };
    }, [planModalOpen, t.failedLoadPlans]);

    useEffect(() => {
        if (!planModalOpen || plansRequested) return;
        if (!user?.token) return;

        setPlansRequested(true);
        setPlansLoading(true);
        setPlansError(null);

        requestData(
            user,
            "pricing_plans",
            { product_id: PRODUCT_ID, lang },
            "list"
        );
    }, [planModalOpen, plansRequested, user, lang]);

    const handlePlanSelect = (planId) => {
        setSelectedPlanId(planId);
        setInvoiceError(null);
    };

    const handlePayInvoice = (invoice) => {
        if (!invoice || payingInvoice) return;
        const invoiceId = invoice.invoice_code || invoice.id;
        if (!invoiceId || !user?.token) {
            setPayError(t.invoiceFailed);
            return;
        }
        setPayError(null);
        setPayingInvoice(true);
        let settled = false;

        const requestId = `invoices:pay-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const responseEvent = "invoices:pay-response";

        const handleResponse = (payload = {}) => {
            if (payload.request_id !== requestId) return;
            socket.off(responseEvent, handleResponse);
            setPayingInvoice(false);
            settled = true;
            if (payload.status === 1) {
                const updatedInvoice = payload.invoice || payload.data || invoice;
                setPreviewInvoice(updatedInvoice);
                requestData(user, object, props.args || {}, "list");
                window.dispatchEvent?.(new CustomEvent("billing:invoices:refreshCounters", { detail: { reason: "paid" } }));
                refreshUser?.();
            } else {
                setPayError(payload?.err?.message || t.invoiceFailed);
            }
        };

        socket.on(responseEvent, handleResponse);
        socket.emit("invoices:pay", {
            request_id: requestId,
            token: user.token,
            invoice_id: invoiceId,
            lang,
        });

        setTimeout(() => {
            socket.off(responseEvent, handleResponse);
            if (!settled) {
                setPayingInvoice(false);
                setPayError(t.invoiceFailed);
            }
        }, 10000);
    };

    const columns = [
        { label: t.colPlan, key: "pricing_plan_name", format: formatPlanRange },
        { label: t.colCost, key: "amount", format: formatPrice },
        { label: t.colStatus, key: "status", format: formatStatus },
    ];

    const fields = [
        {
            type: "tab",
            name: t.tabMain,
            image: "",
            fields: [
                [{ label: t.fProductID, key: "product_id", default_value: PRODUCT_ID, showLabel: "top" }],
                [{ label: t.fWorkspaceID, key: "workspace_id", default_value: "", showLabel: "top" }],
                [{ label: t.fPlanID, key: "pricing_plan_id", default_value: "", showLabel: "top" }],
                [{ label: t.fPlanName, key: "pricing_plan_name", default_value: "", showLabel: "top" }],
                [{ label: t.fAmount, key: "amount", default_value: "", showLabel: "top" }],
                [{ label: t.fCurrency, key: "currency", default_value: "$", showLabel: "top" }],
                [{ label: t.fPeriod, key: "period", default_value: "", showLabel: "top" }],
                [{ label: t.fDescription, key: "description", default_value: "", showLabel: "top", type: "textarea" }],
                [{ label: t.fDueDate, key: "due_date", default_value: "", showLabel: "top", type: "datetime-local" }],
                [{ label: t.fMeta, key: "meta", default_value: "", showLabel: "top", type: "textarea" }],
            ],
        },
    ];

    const actions = [
        { name: "view", label: t.viewAction, controller: "crud", icon: "ti ti-eye" },
        { name: "delete", label: t.cancelAction, controller: "crud", icon: "ti ti-x", fn: (_, item) => (item.status || "").toLowerCase() === "pending" },
    ];

    const renderInvoiceCard = (item, helpers) => {
        const planBlock = formatPlanRange(item);
        const priceBlock = formatPrice(item);
        const statusBlock = formatStatus(item);
        const actionsList = (helpers.actions || []).filter((action) => {
            if (typeof action.fn === "function") {
                return action.fn(action.name, item);
            }
            return true;
        });
        const handleAction = (actionName) => helpers.tableAction?.(actionName, item);

        return (
            <div className="card h-100 shadow-sm border">
                <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        {statusBlock}
                        {priceBlock}
                    </div>
                    <div className="mb-3">{planBlock}</div>
                    <div className="text-muted small mb-4">
                        {item.invoice_code ? (
                            <>
                                {item.invoice_code} · {t.fProductID}: <span className="fw-semibold text-dark">{item.product_id ?? "-"}</span>
                            </>
                        ) : (
                            <>
                                {t.fProductID}: <span className="fw-semibold text-dark">{item.product_id ?? "-"}</span>
                            </>
                        )}
                    </div>

                    <div className="mt-auto d-flex gap-2 justify-content-end flex-wrap">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => openPreview(item)}
                        >
                            <i className="ti ti-eye me-1"></i>
                            {t.viewAction}
                        </button>

                        {actionsList
                            .filter((action) => action.name !== "view")
                            .map((action) => (
                                <button
                                    key={action.name}
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    title={action.label}
                                    onClick={() => handleAction(action.name)}
                                >
                                    <i className={action.icon || "ti ti-dots"}></i>
                                </button>
                            ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <BasicCRUD
                title={t.title}
                subTitle={t.subTitle}
                homeURL={t.homeURL}
                addTitle={t.addTitle}
                editTitle={t.editTitle}
                object={object}
                columns={columns}
                fields={fields}
                actions={actions}
                fieldsData={fieldsData}
                setFieldsData={setFieldsData}
                response={response}
                setResponse={setResponse}
                ownHandler={true}
                viewModes={["table", "blocks"]}
                renderItem={renderInvoiceCard}
                cardLayout={{ columnClass: "col-12 col-lg-6 col-xxl-4" }}
                executeBeforeSave={normalizeFieldsBeforeSave}
                tableAction={handleTableAction}
                customAddFunction={openPlanModal}
            >
                {response.status === 1 ? (
                    <FadeAlert response={response} resetResponse={() => setResponse([])}>
                        {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                        {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                    </FadeAlert>
                ) : null}
            </BasicCRUD>

            {planModalOpen && (
                <Dialog
                    title={t.choosePlanTitle}
                    editTitle={t.choosePlanTitle}
                    customTitle={t.choosePlanTitle}
                    dialogMaxWidth="1300px"
                    fieldsData={{}}
                    closeDialog={closePlanModal}
                    saveDialog={handleConfirmPlan}
                    dialogSize="modal-xl modal-dialog-centered"
                    footerContent={
                        <div className="d-flex flex-wrap align-items-center gap-3 w-100">
                            <div className="flex-grow-1">
                                <div className="text-uppercase text-body-secondary fw-semibold fs-2 mb-1">
                                    {t.selectedPlanLabel}
                                </div>
                                {selectedPlan ? (
                                    <>
                                        <div className="d-flex flex-wrap align-items-baseline gap-3">
                                            <span className="display-6 fw-semibold text-primary mb-0">
                                                {selectedPlan.name}
                                            </span>
                                            <span className="fs-4 fw-semibold text-dark">
                                                {selectedPlan.price}
                                                <span className="text-body-secondary ms-1">{selectedPlan.period}</span>
                                            </span>
                                        </div>
                                        {selectedPlan.description ? (
                                            <p className="text-body-secondary small mb-0">
                                                {selectedPlan.description}
                                            </p>
                                        ) : null}
                                    </>
                                ) : (
                                    <p className="text-body-secondary small mb-0">
                                        {t.selectPlanPlaceholder}
                                    </p>
                                )}
                            </div>
                            <div className="d-flex gap-2 ms-auto flex-shrink-0">
                                <button type="button" className="btn btn-outline-secondary" onClick={closePlanModal}>
                                    {t.maybeLater}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleConfirmPlan}
                                    disabled={!selectedPlan || invoiceSubmitting}
                                >
                                    {invoiceSubmitting
                                        ? t.creatingInvoice
                                        : selectedPlan
                                            ? (t.continueWith?.(selectedPlan.name) || t.addTitle)
                                            : t.selectPlanPlaceholder}
                                </button>
                            </div>
                        </div>
                    }
                    hideActions
                >
                    <p className="text-body-secondary mb-4">{t.choosePlanSubtitle || t.choosePlanTitle}</p>
                    {plansLoading && !pricingPlans.length && (
                        <div className="alert alert-info d-flex align-items-center gap-2 py-2 px-3 mb-4">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span>{t.loadingPlans}</span>
                        </div>
                    )}
                    {plansError && (
                        <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 mb-4">
                            <i className="ti ti-alert-circle"></i>
                            <span>{plansError}</span>
                        </div>
                    )}
                    {invoiceError && (
                        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 px-3 mb-4">
                            <i className="ti ti-alert-triangle"></i>
                            <span>{invoiceError}</span>
                        </div>
                    )}
                    {payError && (
                        <div className="alert alert-danger d-flex align-items-center gap-2 py-2 px-3 mb-4">
                            <i className="ti ti-alert-triangle"></i>
                            <span>{payError}</span>
                        </div>
                    )}
                    <div className="mx-auto" style={{ maxWidth: "1200px" }}>
                        {plansLoading && !pricingPlans.length ? (
                            <div className="row g-4">
                                {Array.from({ length: 3 }).map((_, idx) => (
                                    <div className="col-12 col-md-6 col-xl-4" key={`skeleton-${idx}`}>
                                        <div className="rounded-4 p-4 h-100 position-relative" style={{ background: "#f3f4f6", minHeight: 280 }}>
                                            <div className="placeholder-glow mb-3">
                                                <span className="placeholder col-7"></span>
                                            </div>
                                            <div className="placeholder-glow mb-2">
                                                <span className="placeholder col-5"></span>
                                            </div>
                                            <div className="placeholder-glow mb-4">
                                                <span className="placeholder col-9"></span>
                                                <span className="placeholder col-6"></span>
                                            </div>
                                            <div className="placeholder-wave">
                                                <span className="placeholder col-12 mb-2"></span>
                                                <span className="placeholder col-10 mb-2"></span>
                                                <span className="placeholder col-8 mb-2"></span>
                                            </div>
                                            <div className="placeholder-glow mt-4">
                                                <span className="placeholder col-6"></span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="row g-4">
                                {planOptions.map((plan) => (
                                    <PlanCard
                                        key={plan.id}
                                        plan={plan}
                                        selected={selectedPlan?.id === plan.id}
                                        onSelect={() => handlePlanSelect(plan.id)}
                                        selectLabel={t.btnSelectPlan}
                                        selectedLabel={t.btnSelected}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </Dialog>
            )}

            {previewInvoice && (
                <InvoicePreviewDialog
                    invoice={previewInvoice}
                    t={t}
                    onClose={closePreview}
                    onPay={handlePayInvoice}
                    paying={payingInvoice}
                    payError={payError}
                />
            )}
        </>
    );
};

export default Invoices;
